import React, { FC, useEffect, useState } from "react";
import { useNavigate, NavigationType, useNavigationType } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import { Validation } from "../../utils/validations";
import toast from "react-hot-toast";
import Loading from "shared/Loading/Loading";
import { MainTemplate } from "templates/MainTemplate";
import { fetchCardBin, fetchSubscription } from "services/fetch-subscription";
import RenderSidebar from "./RenderSidebar";
import RenderMain from "./RenderMain";
import { Subscription } from "./type";
import { useMetadataHotel } from "contexts/metadata-hotel-context";
import { Plan } from "containers/PageSubcription/type";

interface CheckOutPagePageMainProps {
  className?: string;
}

const CheckOutPagePageMain: FC<CheckOutPagePageMainProps> = ({
  className = "",
}) => {
  const [infoCardFocus, setInfoCardFocus] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const {
    planId,
    planName,
    monthlyValue,
    description,
    points
  } = useMetadataHotel();
  const planDetails: Plan = {
    id: planId,
    name: planName,
    monthly_value: monthlyValue,
    description,
    points
  }
  const navigate = useNavigate();
  const navigationType = useNavigationType();

  const validationSchema = yup.object({
    number: yup.string().required("Número do cartão é obrigatório"),
    name: yup.string().required("Nome do titular é obrigatório"),
    expiry: yup.string()
      .test("is-valid-date", "Data inválida", (value) => {
        return value ? Validation.isValidDateCredit(value) : false;
      })
      .required("Validade é obrigatório"),
    cvc: yup.string().required("CVC é obrigatório")
  });

  const formik = useFormik({
    initialValues: {
      number: "",
      name: "",
      expiry: "",
      cvc: ""
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      const brand: any = await getBrand(
        values.number.split(" ").join("").substring(0, 6)
      )

      if (brand.response.status !== 200) {
        setLoading(false);
        toast.error(brand.detail, { duration: 10000 });
        return;
      }

      const params = {
        plan_id: planId,
        payment: {
          card_number: values.number.split(" ").join(""),
          holder: values.name,
          expiration_date: values.expiry,
          security_code: values.cvc,
          brand: brand?.rest?.provider
        }
      };

      const subscription = await createSubscription(params)

      if (subscription) {
        setLoading(false);
        navigate("/pay-done");
      }
    },
  });

  const getBrand = async (number: string) => {
    const brand = await fetchCardBin(number);
    return brand
  }

  const createSubscription = async (params: Subscription) => {
    try {
      const subscription = await fetchSubscription(params)
      return subscription
    } catch (error) {
      setLoading(false);
      toast.error(
        "Erro no pagamento: verifique seus dados e tente novamente"
      );
    }
  }

  useEffect(() => {
    // Se não houver detalhes do plano, redirecione para a página de planos
    if (!planDetails || navigationType === NavigationType.Pop) {
      return navigate("/home");
    }
  }, []);

  const handleError = (fieldName: keyof any, formik: any) => {
    if (formik.touched[fieldName] && Boolean(formik.errors[fieldName])) {
      return `mt-1 border border-red-500 focus:ring-2 focus:outline-none focus:ring-red-500 focus:border-red-500`;
    }
    return `mt-1 border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent`;
  };

  return (
    <MainTemplate>
      <div className={`nc-CheckOutPagePageMain ${className}`}>
        <main className="container mt-11 mb-24 lg:mb-32 flex flex-col-reverse lg:flex-row">
          {loading ? (<Loading size={70} />) : (
            <>
              <div className="w-full lg:w-3/5 xl:w-2/3 lg:pr-10 ">
                 <RenderMain
                    infoCardFocus={infoCardFocus}
                    formik={formik}
                    planDetails={planDetails}
                    handleError={handleError}
                    handleInputFocus={
                      (event: React.FocusEvent<HTMLInputElement>) => 
                      setInfoCardFocus(event.target.name)
                    }
                 />
              </div>
              <div className="hidden lg:block flex-grow">
                <RenderSidebar
                  infoCardFocus={infoCardFocus}
                  formik={formik}
                  planDetails={planDetails}
                />
              </div>
            </>
          )}
        </main>
      </div>
    </MainTemplate>
  );
};

export default CheckOutPagePageMain;
